import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';

// These environment variables are set in .env
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

const hasSupabaseConfig = !!(supabaseUrl && supabaseAnonKey);

if (!hasSupabaseConfig) {
  console.warn('Missing Supabase environment variables.');
}

export const supabase = createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      // Supabase Auth manages JWT sessions. detectSessionInUrl lets the
      // password-recovery/invite links (admin-generated) establish a session.
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true
    },
    db: {
      schema: 'public'
    }
  }
);

// Helper function to check if Supabase connection is available
export const checkSupabaseConnection = async (): Promise<boolean> => {
  if (!hasSupabaseConfig) return false;

  try {
    // Lightweight reachability check. Any structured response from PostgREST —
    // including an RLS/permission error — proves the server is reachable. Only a
    // thrown network error (caught below) means we're actually disconnected.
    await supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true });

    return true;
  } catch (err) {
    console.error('Error checking Supabase connection:', err);
    return false;
  }
};

// Helper function to verify database schema (runs checks in parallel)
export const verifyDatabaseSchema = async (): Promise<boolean> => {
  if (!hasSupabaseConfig) return false;

  const requiredTables = [
    'coaches',
    'coach_squads',
    'players',
    'sessions',
    'session_squads',
    'session_coaches',
    'attendance',
    'documents',
    'squads'
  ];

  try {
    const results = await Promise.all(
      requiredTables.map(table =>
        supabase.from(table).select('count', { count: 'exact', head: true })
      )
    );

    for (let i = 0; i < results.length; i++) {
      if (results[i].error?.code === '42P01') {
        console.warn(`Missing required table: ${requiredTables[i]}`);
        return false;
      }
    }

    return true;
  } catch (err) {
    console.error('Error verifying database schema:', err);
    return false;
  }
};

// Helper type for handling Supabase errors
export type SupabaseError = {
  code: string;
  message: string;
  details?: string;
} | null;
